import classNames from 'classnames';
import { map, reduce } from 'lodash';
import { linkToMeetingSpacePage } from '../../../../common/services/url-builder';
import replacement from '../../../../react/services/Replacement';
import { preventJavascriptUrls } from '../../../../services/security';
import { getCurrentUser } from '../../../services/CurrentUser';
import { getUrl } from '../../../services/Cycle';
import { modulo } from '../../../services/Math';
import { trackEvent } from '../../../services/Tracking';
import { getUrlToProfile, isManager } from '../../../services/User';
import styles from './style.m.less';

export default function ResultList({ items, selectedItem, totalNumberOfItems, doHideResults, doSelectItem, mode }) {
    const preProcessedItems = preProcess(mode, items);
    const nonSelectable = reduce(
        preProcessedItems,
        ({ result, index }, item) => {
            if (item.type === 'HEADER') {
                result.push(index);
            }
            return { result, index: ++index };
        },
        { result: [], index: 0 }
    ).result;
    const isSelectedConfigured = isSelected(nonSelectable);

    return (
        <ul className={classNames('nakedList', styles.list)} id="header_search_bar-result">
            {map(preProcessedItems, (item, index) => (
                <ResultItem
                    key={item.id}
                    item={item}
                    doSelectItem={() => doSelectItem(compensateForNonSelectable(index, nonSelectable), true)}
                    itemIsSelected={isSelectedConfigured(index, selectedItem, totalNumberOfItems)}
                    doHideResults={() => {
                        trackEvent({
                            category: 'navigation header search bar',
                            action: 'click search item',
                            label: item.type.toLowerCase(),
                        });
                        return doHideResults();
                    }}
                />
            ))}
        </ul>
    );
}

function isSelected(nonSelectable) {
    return (index, selectedItem, totalNumberOfItems) => {
        if (selectedItem === null) {
            return false;
        } else {
            return compensateForNonSelectable(index, nonSelectable) === modulo(selectedItem, totalNumberOfItems);
        }
    };
}

function compensateForNonSelectable(index, nonSelectable) {
    const nrNonSelectable = reduce(
        nonSelectable,
        (memo, indexOfNonSelectable) => {
            return indexOfNonSelectable < index ? memo + 1 : memo;
        },
        0
    );
    return index - nrNonSelectable;
}

function ResultItem({ item, doHideResults, itemIsSelected, doSelectItem }) {
    return {
        TEAM: (
            <Team
                item={item.value}
                doHideResults={doHideResults}
                doSelectItem={doSelectItem}
                itemIsSelected={itemIsSelected}
            />
        ),
        CYCLE_SPECIFIC_REVIEWEES: (
            <Team
                item={item.value}
                doHideResults={doHideResults}
                doSelectItem={doSelectItem}
                itemIsSelected={itemIsSelected}
            />
        ),
        CYCLE: (
            <Cycle
                item={item.value}
                doHideResults={doHideResults}
                doSelectItem={doSelectItem}
                itemIsSelected={itemIsSelected}
            />
        ),
        SEARCH: (
            <Search
                item={item}
                doHideResults={doHideResults}
                doSelectItem={doSelectItem}
                itemIsSelected={itemIsSelected}
            />
        ),
        HEADER: <Header item={item} />,
        NOTHING_FOUND: <NoResults />,
    }[item.type];
}

function Header({ item }) {
    return (
        <li className={styles.segmentTitle}>
            <small>{item.value}</small>
        </li>
    );
}

function NoResults() {
    return (
        <li className={styles.noResults}>
            <small>No Results</small>
        </li>
    );
}

function Team({ item, doHideResults, itemIsSelected, doSelectItem }) {
    const props = { item, doHideResults, itemIsSelected, doSelectItem };

    if (item.isSecondaryReviewee) {
        return <SecondaryReviewee {...props} />;
    } else if (isManager(getCurrentUser())) {
        return <DirectReport {...props} />;
    } else {
        return <Teammate {...props} />;
    }
}

function DirectReport({ item, doHideResults, itemIsSelected, doSelectItem }) {
    const actions = getReportLinks(item, { isDirectReport: true });
    const props = { item, doHideResults, itemIsSelected, doSelectItem, actions };
    return <UserWithActions {...props} />;
}

function SecondaryReviewee({ item, doHideResults, itemIsSelected, doSelectItem }) {
    const actions = getReportLinks(item, { isDirectReport: false });
    const props = { item, doHideResults, itemIsSelected, doSelectItem, actions };
    return <UserWithActions {...props} />;
}

function Teammate({ item, doHideResults, itemIsSelected, doSelectItem }) {
    const itemClasses = classNames(styles.item, {
        [styles.itemSelected]: itemIsSelected,
    });

    return (
        <li className={styles.listItem}>
            <a
                tabIndex="-1"
                href={getUrlToProfile(item)}
                onClick={doHideResults}
                className={itemClasses}
                onMouseOver={doSelectItem}
            >
                <div className={styles.imageContainer}>
                    <img src={item.logo} className={styles.image} alt="" />
                </div>
                <div className={styles.info}>
                    <span className={styles.title}>{item.name}</span>
                    <span className={styles.subTitle}>{item.role}</span>
                </div>
            </a>
        </li>
    );
}

function UserWithActions({ item, doHideResults, itemIsSelected, doSelectItem, actions }) {
    const itemClasses = classNames(styles.item, {
        [styles.itemSelected]: itemIsSelected,
    });
    const href = getUrlToProfile(item);

    return (
        <li className={itemClasses} onMouseOver={doSelectItem}>
            <a
                tabIndex="-1"
                href={preventJavascriptUrls(href)}
                onClick={doHideResults}
                className={styles.imageContainer}
            >
                <img src={item.logo} className={styles.image} alt="" />
            </a>
            <div className={styles.info}>
                <a
                    tabIndex="-1"
                    className={styles.titleTeam}
                    href={preventJavascriptUrls(href)}
                    onClick={doHideResults}
                >
                    {item.name}
                </a>
                <div className={styles.actions}>
                    {map(actions, action => (
                        <a
                            tabIndex="-1"
                            className={styles.action}
                            href={preventJavascriptUrls(action.url)}
                            key={action.url}
                            onClick={doHideResults}
                        >
                            {action.name}
                        </a>
                    ))}
                </div>
            </div>
        </li>
    );
}

function getReportLinks(teamMember, { isDirectReport }) {
    const enabledFeatures = getCurrentUser().company.enabledFeatures;
    const links = [];

    if (enabledFeatures.objective) {
        links.push({
            name: replacement('objective.plural.big'),
            url: '/app/user/' + teamMember.id + '/?tab=objectives',
        });
    }
    if (enabledFeatures.feedback) {
        links.push({
            name: replacement('fr.short.big'),
            url: '/app/user/' + teamMember.id + '/?tab=360s',
        });
    }
    if (enabledFeatures.review) {
        links.push({
            name: replacement('pr.plural.short'),
            url: '/app/user/' + teamMember.id + '/?tab=reviews',
        });
    }
    if (enabledFeatures.message) {
        links.push({
            name: 'Messages',
            url: '/app/user/' + teamMember.id + '/?tab=messages',
        });
    }
    if (enabledFeatures.meetings && isDirectReport) {
        links.push({
            name: '1:1s',
            url: linkToMeetingSpacePage(teamMember),
        });
    }

    return links;
}

function Cycle({ item, doHideResults, itemIsSelected, doSelectItem }) {
    const itemClasses = classNames(styles.item, styles.itemSmall, {
        [styles.itemSelected]: itemIsSelected,
    });

    return (
        <li className={itemClasses} onMouseOver={doSelectItem}>
            <a
                tabIndex="-1"
                href={preventJavascriptUrls(getUrl(getCurrentUser(), item))}
                className={styles.linkSmall}
                onClick={doHideResults}
            >
                <div className={styles.mainContent}>{item.name}</div>
            </a>
        </li>
    );
}

function Search({ item, doHideResults, itemIsSelected, doSelectItem }) {
    const itemClasses = classNames(styles.item, {
        [styles.itemInactive]: !item.value.data.active,
        [styles.itemSelected]: itemIsSelected,
    });

    return (
        <li className={styles.listItem}>
            <a
                tabIndex="-1"
                href={preventJavascriptUrls(item.value.url)}
                className={itemClasses}
                onMouseOver={doSelectItem}
                onClick={doHideResults}
                id={itemIsSelected ? 'header_search_bar-result--selected-item' : ''}
                aria-label={`${item.value.plainValue}, ${item.value.plainRole}`}
            >
                <div className={styles.imageContainer}>
                    <img src={item.value.data.logoUrl} className={styles.image} alt="" />
                </div>
                <div className={styles.info}>
                    <span className={styles.title} dangerouslySetInnerHTML={{ __html: item.value.formattedValue }} />
                    <span className={styles.subTitle} dangerouslySetInnerHTML={{ __html: item.value.formattedRole }} />
                </div>
                <strong className={styles.label}>{item.value.typeFormatted}</strong>
            </a>
        </li>
    );
}

function preProcess(mode, items) {
    if (items.length === 0) {
        return [
            {
                type: 'NOTHING_FOUND',
                id: 'NOTHING_FOUND',
            },
        ];
    } else {
        return (
            {
                CYCLE: intersperseHeadingsToCycles(items),
                TEAM: intersperseHeadingsToTeam(items),
            }[mode] || items
        );
    }
}

function intersperseHeadingsToCycles(items) {
    const getHeading = item => ({
        type: 'HEADER',
        id: getHeader(item.value.type),
        value: getHeader(item.value.type),
    });

    return intersperseHeadings(item => item.value.type, getHeading, items);
}

function intersperseHeadings(getCategory, getHeading, items) {
    const withHeadings = reduce(
        items,
        (memo, item) => {
            if (getCategory(item) && !memo.categoriesDiscovered[getCategory(item)]) {
                memo.result.push(getHeading(item));
                memo.categoriesDiscovered[getCategory(item)] = true;
            }
            memo.result.push(item);
            return memo;
        },
        { categoriesDiscovered: {}, result: [] }
    );

    return withHeadings.result;
}

function intersperseHeadingsToTeam(items) {
    const getType = item => {
        if (item.value.isSecondaryReviewee) {
            return 'SECONDARY';
        } else if (item.type === 'CYCLE_SPECIFIC_REVIEWEES') {
            return item.type;
        } else {
            return null;
        }
    };
    const getHeading = item => ({
        type: 'HEADER',
        id: getHeader(getType(item)),
        value: getHeader(getType(item)),
    });

    return intersperseHeadings(getType, getHeading, items);
}

function getHeader(type) {
    switch (type) {
        case 'REVIEW':
            return replacement('pr.singular.short') + ' Cycles';
        case 'OBJECTIVE':
            return replacement('objective.singular.big') + ' Cycles';
        case 'FEEDBACK':
            return replacement('fr.short.big') + ' Cycles';
        case 'SECONDARY':
            return 'Secondary Reviewees';
        case 'CYCLE_SPECIFIC_REVIEWEES':
            return 'Cycle Specific Reviewees';
    }
}
